import { StudentApplication, UploadFileData } from '../components/Application/types'
import { DocumentTags } from './enums'

/** Matches `uploadStatus` on application file metadata from the students API (JSON:API attributes.files). */
const FILE_UPLOAD_STATUS_UPLOADED = 'UPLOADED'

/**
 * Timeline / server-side check: every education history entry must reference at least one transcript
 * or certificate, and each referenced file must exist on the application with uploadStatus UPLOADED.
 */
export function areEducationHistoryServerDocumentsFullyUploaded(
  application: StudentApplication,
): boolean {
  const attrs = application.attributes
  if (!attrs?.educationHistory) {
    return false
  }

  const files = attrs.files ?? {}
  const entries = Object.values(attrs.educationHistory).filter(
    (entry): entry is NonNullable<typeof entry> => entry !== null,
  )

  if (entries.length === 0) {
    return false
  }

  for (const entry of entries) {
    const transcriptIds = entry.transcripts ?? []
    const certificateIds = entry.certificates ?? []

    if (transcriptIds.length === 0 && certificateIds.length === 0) {
      return false
    }

    for (const fileId of transcriptIds) {
      if (fileId === undefined || fileId === '') {
        return false
      }
      const file = files[fileId]
      if (
        !file ||
        String(file.type) !== DocumentTags.TRANSCRIPTS ||
        file.uploadStatus !== FILE_UPLOAD_STATUS_UPLOADED
      ) {
        return false
      }
    }

    for (const fileId of certificateIds) {
      if (fileId === undefined || fileId === '') {
        return false
      }
      const file = files[fileId]
      if (
        !file ||
        String(file.type) !== DocumentTags.CERTIFICATE ||
        file.uploadStatus !== FILE_UPLOAD_STATUS_UPLOADED
      ) {
        return false
      }
    }
  }

  return true
}

type ObservableFilesGetter = (filters: {
  fileType?: string
  sectionReference?: string
}) => Record<string, unknown>

/**
 * Form submit check: observable files for a section that already exist on the application must be UPLOADED.
 * Entries only in pendingUpload (not yet PATCHed) are skipped so Save & Continue can run the upload flow.
 */
export function areEducationHistoryObservableFilesUploaded(
  application: StudentApplication,
  pendingUpload: Record<string, UploadFileData>,
  getObservableFiles: ObservableFilesGetter,
  fileType: DocumentTags,
  sectionReference: string,
): boolean {
  const observable = getObservableFiles({ fileType, sectionReference })
  const serverFiles = application.attributes?.files ?? {}

  for (const fileId of Object.keys(observable)) {
    if (pendingUpload[fileId]) {
      continue
    }
    const meta = serverFiles[fileId] as { uploadStatus?: string } | undefined
    if (!meta || meta.uploadStatus !== FILE_UPLOAD_STATUS_UPLOADED) {
      return false
    }
  }

  return true
}
